import React, { useEffect } from "react";
import { Phone, MessageCircle, Calculator, Star, Award, Clock } from "lucide-react";
import { useAnalytics } from "../hooks/useAnalytics";

const GoogleAdsLandingSection = () => {
  const { trackWhatsAppClick } = useAnalytics();

  useEffect(() => {
    // Track Google Ads landing page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Google Ads Landing',
        page_location: window.location.href,
        custom_parameter_1: 'google_ads_traffic'
      });
    }
  }, []);

  const handleWhatsAppClick = () => {
    // Track WhatsApp click with Google Ads conversion
    trackWhatsAppClick('google_ads_landing');

    const message = `Hallo! Ich komme von Google Ads und interessiere mich für Ihre Malerarbeiten.

Können Sie mir bitte ein Festpreis-Angebot machen?

Vielen Dank!`;

    // Use window.open for reliability - this always works even if tracking fails
    window.open(`https://wa.me/4915758183212?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handlePhoneClick = () => {
    // Track phone click (separate from WhatsApp)
    if (window.gtag) {
      window.gtag('event', 'phone_click', {
        event_category: 'engagement',
        event_label: 'google_ads_landing_phone',
        send_to: 'G-1PCNWT67T9'
      });
    }
  };

  return (
    <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-orange-500 text-white py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero for Google Ads Traffic */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
            <Star className="w-4 h-4 text-yellow-300" />
            <span className="text-sm font-medium">Willkommen!</span>
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-bold mb-4">
            Maler in Baden-Württemberg – Wohnung streichen zum Festpreis
          </h1>
          <p className="text-xl text-blue-100 mb-6">
            Professionelle Malerarbeiten mit Airless-Technik. Festpreis inkl. Material und Abdeckarbeiten.
          </p>

          {/* Trust Signals */}
          <div className="flex items-center justify-center gap-6 mb-8 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-yellow-300" />
              <span>Festpreis-Garantie</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-300" />
              <span>Schnelle Rückmeldung</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-300" />
              <span>Ganz Baden-Württemberg</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            
            {/* WhatsApp CTA */}
            <button
              onClick={handleWhatsAppClick}
              className="flex-1 flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
            >
              <MessageCircle className="w-6 h-6" />
              <div className="text-left">
                <div>WhatsApp Sofort</div>
                <div className="text-sm opacity-90">Angebot in 5 Min</div>
              </div>
            </button>

            {/* Phone CTA */}
            <a
              href="tel:+4915758183212"
              onClick={handlePhoneClick}
              className="flex-1 flex items-center justify-center gap-3 bg-white/20 hover:bg-white/30 border-2 border-white/50 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
            >
              <Phone className="w-6 h-6" />
              <div className="text-left">
                <div>Jetzt Anrufen</div>
                <div className="text-sm opacity-90">+49 157 5818 3212</div>
              </div>
            </a>
          </div>
        </div>

        {/* Quick Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <Calculator className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
            <h3 className="font-bold mb-1">Festpreis-System</h3>
            <p className="text-sm text-blue-100">Transparente Preise ohne Überraschungen</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <Award className="w-8 h-8 mx-auto mb-2 text-green-300" />
            <h3 className="font-bold mb-1">Airless-Technik</h3>
            <p className="text-sm text-blue-100">Professionelle Ergebnisse in Rekordzeit</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <Star className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
            <h3 className="font-bold mb-1">Baden-Württemberg</h3>
            <p className="text-sm text-blue-100">Von Freiburg bis Mannheim im Einsatz</p>
          </div>
        </div>

        {/* Urgency */}
        <div className="text-center mt-6">
          <p className="text-yellow-200 font-medium">
            🔥 Begrenzte Verfügbarkeit: Nur noch wenige Termine diese Woche!
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleAdsLandingSection;